import type { MarketplaceConfig } from "@slfinrtl/feature-config"
import { dequal } from "dequal/lite"
import transform from "lodash-es/transform"

const MAX_ATTEMPTS = 10

export type TransformationRules<FormValues> = TransformationRule<FormValues>[]

interface TransformationRule<FormValues> {
	effect: Partial<FormValues>
	trigger: Partial<FormValues> | TransformationFunction<FormValues> | OneOf<FormValues> | Not<FormValues>
}

interface OneOf<FormValues> {
	$oneOf: { key: keyof FormValues; values: any[] }
}
interface Not<FormValues> {
	$not: Partial<FormValues>
}

type TransformationFunction<FormValues> = (values: FormValues, config: MarketplaceConfig) => boolean

export function transformValues<FormValues>(
	rules: TransformationRules<FormValues>,
	value: FormValues,
	marketplaceConfig: MarketplaceConfig,
	attempt = 0,
): FormValues {
	const combinedEffects = getMatchingRules(rules, value, marketplaceConfig)
		.map((rule) => normalizeEffect(rule.effect, value))
		.reduce<Partial<FormValues>>((allEffects, effect) => ({ ...allEffects, ...effect }), {})

	const result = { ...value, ...combinedEffects }
	return dequal(result, value) || attempt === MAX_ATTEMPTS
		? result
		: transformValues(rules, result, marketplaceConfig, attempt + 1)
}

function getMatchingRules<FormValues>(
	transformationRules: TransformationRules<FormValues>,
	value: FormValues,
	config: MarketplaceConfig,
) {
	return transformationRules.filter((rule) => {
		if (typeof rule.trigger === "function") {
			// check if function returns true
			return rule.trigger(value, config)
		}
		if (typeof rule.trigger === "object") {
			// check if rule is completely part of values
			return Object.entries(rule.trigger).every(([triggerKey, triggerValue]) => {
				if (triggerKey === "$oneOf") {
					return (triggerValue as OneOf<FormValues>["$oneOf"]).values.some(
						(item) => value[(triggerValue as OneOf<FormValues>["$oneOf"]).key] === item,
					)
				}
				if (triggerKey === "$not") {
					return Object.entries(triggerValue as Not<FormValues>["$not"]).every(
						([key, val]) => value[key as keyof typeof value] !== val,
					)
				}
				return value[triggerKey as keyof typeof value] === triggerValue
			})
		}
		return undefined
	})
}

function normalizeEffect<FormValues>(effect: Partial<FormValues>, value: FormValues) {
	// When an effect should set a value to null but the value is undefined,
	// it should leave it as it is, since setting values to null is only relevant for PATCHING/resetting existing values
	return transform(
		effect,
		(result, val, key: keyof FormValues) => {
			if (val === null && value[key] === undefined) {
				result[key] = undefined
			}
			return result
		},
		{ ...effect },
	)
}
